<template>
  <CartItemShimmer v-if="pending || !customisedProduct" />
  <div
    v-else
    :class="bem({ isLoading, soldOut })"
  >
    <div :class="bem('container')">
      <aside :class="bem('image-wrapper')">
        <component
          :is="url ? VisionLink : 'div'"
          :to="url"
          :class="{ 'vision-shimmer': !imageLoaded }"
        >
          <DynamicImg
            :sku="imageSku"
            :secondary-sku="customisedProduct?.secondaryLegacySKU"
            :height="800"
            :width="800"
            bg="f7f7fa"
            :view-angle="5"
            :class="bem('product-image')"
            @loaded="handleImageLoad"
          />
        </component>
      </aside>
      <div :class="bem('body')">
        <div :class="bem('body-head')">
          <component
            :is="url ? VisionLink : 'div'"
            :to="url"
            :data-testId="`cart-item-heading-${cartItem.bundleBaseSku}`"
            display="block"
            :class="bem('body-head-title-container')"
          >
            <VisionHeading
              v-if="customisedProduct.isPrescription"
              tag="h6"
              font-weight="light"
              colour-style="grey"
              :class="bem('title-type')"
            >
              Prescription
            </VisionHeading>
            <VisionHeading
              tag="h2"
              size="h4"
              :class="bem('title')"
            >
              <span v-if="customisedProduct.isCustom" :class="bem('custom-title-prefix')">
                Custom
              </span>
              <!-- eslint-disable vue/no-v-html -->
              <span v-html="productName" />
              <!-- eslint-enable vue/no-v-html -->
            </VisionHeading>
            <VisionHeading
              v-if="customisedProduct.subTitle"
              tag="h4"
              size="h5"
              font-weight="light"
              colour-style="grey"
              :class="bem('sub-title')"
            >
              <!-- eslint-disable vue/no-v-html -->
              <span v-html="customisedProduct.subTitle" />
              <!-- eslint-enable vue/no-v-html -->
            </VisionHeading>
          </component>

          <VisionHeading
            v-if="!soldOut"
            tag="h3"
            size="h5"
            :class="[
              bem('price-box', { isLoading }),
              { 'vision-shimmer': isLoading },
            ]"
          >
            <span
              v-if="props.cartItem.bundleCostDifference || 0 < 0"
              :class="bem('original-price')"
            >
              <s>
                {{ $tca(props.cartItem.bundleOriginalCost) }}
              </s>
            </span>
            <span :class="bem('price')">
              <template v-if="props.cartItem.bundleCost === 0"> FREE </template>
              <template v-else>
                {{ $tca(props.cartItem.bundleCost) }}
              </template>
            </span>
          </VisionHeading>
        </div>

        <div :class="bem('body-main')">
          <CartItemPartsBreakdown
            v-if="
              customisedProduct.choicesForDisplay.length &&
                !customisedProduct.isPrebuiltVariant &&
                !soldOut
            "
            :customised-product="customisedProduct"
          />
          <span
            v-else-if="customisedProduct.isPrebuiltVariant"
            :class="bem('prebuilt-variant-name')"
          >
            {{ customisedProduct.matchingPrebuiltVariant.title }}
          </span>
          <VisionTag
            v-else-if="
              customisedProduct.isLimitedEdition &&
                !customisedProduct.sku.includes('lenscleankit') &&
                !customisedProduct.sku.includes('egiftcard') &&
                !soldOut
            "
            size="small"
            text="Limited Edition"
            theme="gradient"
            :class="bem('le-tag')"
          />
          <div
            v-else-if="soldOut"
            :class="bem('out-of-stock-notice')"
          >
            This product has sold out.
          </div>

          <div>
            <div
              v-if="customisedProduct.product.lineItemContent?.length"
              :class="bem('extra-content')"
            >
              <VisionLink
                :class="bem('extra-content-title')"
                line-link
                is-button
                theme="grey"
                @click="
                  showCartItemExpandedContent = !showCartItemExpandedContent
                "
              >
                {{ customisedProduct.product.lineItemContent[0].title }}
              </VisionLink>
              <VisionModal
                :open="showCartItemExpandedContent"
                @close="showCartItemExpandedContent = false"
              >
                <div :class="bem('extra-content-copy')">
                  <DatoStructuredText
                    :content="
                      customisedProduct.product.lineItemContent[0].content
                    "
                    copy-size="body-sm"
                  />
                </div>
              </VisionModal>
            </div>
          </div>

          <CartItemQtySelector
            :current-qty="props.cartItem.bundleQuantity"
            :allow-qty-change="allowQtyChange"
            :line-id="props.cartItem.bundleId"
            :sold-out="soldOut"
            :class="bem('qty-selector')"
            @change="handleQtyChange"
            @remove-item="handleRemoveItem"
          />
        </div>
      </div>
    </div>
    <CartItemLensTechUpsell
      v-if="
        (lensTechUpsell.sku && showlensTechUpsellOption) ||
          (hasUpgradedDetails && props.cartItem.bundleQuantity < 2)
      "
      :customised-product="customisedProduct"
      :lens-tech-upsell="lensTechUpsell"
      :lens-tech-downgrade="lensTechDowngrade"
      :selected="!!hasUpgradedDetails"
      @update-lens-choice="handleLensTechUpsellClick"
    />

    <CartItemCapsuleCaseUpsell
      v-if="!soldOut && false"
      :customised-product="customisedProduct"
      :display-order="props.cartItem.bundleDisplayOrder"
      :cart-item="props.cartItem"
    />

    <CartItemCleaningKitUpsell
      v-if="!soldOut"
      :customised-product="customisedProduct"
      :display-order="props.cartItem.bundleDisplayOrder"
      :cart-item="props.cartItem"
    />

    <CartItemLensKitUpsell
      v-if="!customisedProduct.isPrescription"
      :customised-product="customisedProduct"
      :display-order="props.cartItem.bundleDisplayOrder"
    />
  </div>
</template>

<script setup lang="ts">
import { VisionLink } from '#components'
import {
  getClosestLensTechs,
  getLensOptionFromSku,
} from '~/assets/js/helpers/product'
import { buildCartItems } from '~/assets/js/helpers/shopify'
import { buildQtyChangePayload, type Bundle } from '~/assets/js/helpers/shopify'

interface Props {
  cartItem: {
    bundle: Bundle
    bundleItems: Array<object>
    bundleId: string
    bundleSku: string
    bundleBaseSku: string
    bundleCost: number
    bundleOriginalCost: number
    bundleCostDifference: number
    bundleQuantity: number
    bundleIsGiftCard: boolean
    bundleDisplayOrder: number
  }
  lensTechUpsellSkus: {
    oldSku: string
    newSku: string
  }[]
}

const { $product, $sentry, $track } = useNuxtApp()
const bem = useBEM('CartItem')
const shopifyStore = useShopifyStore()
const alertStore = useAlertStore()

const emits = defineEmits(['polarisation-upgrade'])

const props = withDefaults(defineProps<Props>(), {
  cartItem: () => ({
    bundle: {} as Bundle,
    bundleItems: [],
    bundleId: '',
    bundleSku: '',
    bundleBaseSku: '',
    bundleCost: 0,
    bundleOriginalCost: 0,
    bundleCostDifference: 0,
    bundleQuantity: 1,
    bundleDisplayOrder: 0,
    bundleIsGiftCard: false,
  }),
  lensTechUpsellSkus: undefined,
})

const customisedProduct = ref<any>(null)
const pending = ref<boolean>(true)
const isInvalid = ref<boolean>(false)
const currentLensOptions = ref<any>([])
const currentLensTech = ref<string>('')
const currentLensSku = ref<string>('')
const showCartItemExpandedContent = ref<boolean>(false)
const isLoading = ref<boolean>(false)
const imageLoaded = ref<boolean>(false)
const automaticDiscountsForProduct = ref()

onMounted(async () => {
  if (props.cartItem.bundleIsGiftCard) {
    customisedProduct.value = await $product.getCustomisedProduct('egiftcard', {
      sku: 'egiftcard',
    })

    pending.value = false
  } else if (props.cartItem.bundleBaseSku) {
    try {
      customisedProduct.value = await $product.getCustomisedProduct(
        props.cartItem.bundleBaseSku,
        {
          sku: props.cartItem.bundleSku,
          lensTechSelected: true,
          isPrebuiltVariant:
            props.cartItem.bundle?.summary?.bundleIsPrebuiltVariant,
        },
        true
      )
  
      automaticDiscountsForProduct.value = shopifyStore.getDiscountsFromProduct(customisedProduct.value)
      pending.value = false
    } catch (error) {
      console.error(error)
      $sentry.captureException(error)
      isInvalid.value = true
    }
  } else {
    $sentry.captureException('Bundle SKU is missing. Cannot show cart item.')
  }

  // If the product is invalid then remove it from cart
  if (isInvalid.value) {
    await shopifyStore.removeCartItems([props.cartItem.bundleSku])

    alertStore.ADD_ERROR_MESSAGE({
      body: 'There was an error with one of the products in your cart. Please try again.'
    })
  }
})

const url = computed(() => {
  if (!customisedProduct.value?.product?.hasPdp) return null

  const url = new URL(customisedProduct.value.url, window.location.origin)
  url.searchParams.set('rlt', '1')

  return url.toString()
})

const productName = computed(() => {
  if (
    customisedProduct.value?.isLimitedEdition &&
    customisedProduct.value?.product?.name &&
    customisedProduct.value?.sku.startsWith('microfib')
  )
    return customisedProduct.value.product.name

  if (
    customisedProduct.value.isLimitedEdition &&
    !customisedProduct.value.product.useBaseProductName
  )
    return customisedProduct.value.product.name

  return customisedProduct.value.isLimitedEdition &&
    customisedProduct.value.product?.baseProduct?.name
    ? customisedProduct.value.product.baseProduct.name
    : customisedProduct.value.product.name
})

const imageSku = computed(() => {
  let sku = props.cartItem.bundleSku || customisedProduct.value.sku

  if (customisedProduct.value.isPrebuiltVariant) sku = customisedProduct.value.matchingPrebuiltVariant.customSKU
  
  return sku
})

const soldOut = computed(() => {
  return (
    !customisedProduct.value.preorderPartSelected &&
    !customisedProduct.value.isInStock
  )
})

const allowQtyChange = computed(() => {
  const disallowedSKUs = [
    'microfiber-le_pow',
    'poster-le_woodsy',
    'breakawayclub',
    'pgiftcard',
    'giftwrap',
    'egiftcard',
    'microfibre-le_investor22',
    'cap-le_ineosgrenadiers',
  ]

  if (
    disallowedSKUs.some(
      (sku) =>
        sku === customisedProduct.value.sku ||
        customisedProduct.value.sku.startsWith(`${sku}-`)
    )
  )
    return false

  if (customisedProduct.value.isPrescription) return false

  return true
})

const hasUpgradedDetails = computed(() => {
  return props.lensTechUpsellSkus.find(
    (upsell) => upsell.newSku === props.cartItem.bundleSku
  )
})

const lensTechDowngrade = computed(() => {
  if (hasUpgradedDetails.value?.oldSku && customisedProduct.value) {
    return getLensOptionFromSku(
      hasUpgradedDetails.value?.oldSku,
      customisedProduct.value
    )
  } else if (customisedProduct.value) {
    return getClosestLensTechs(customisedProduct.value).downgrade
  }

  return {}
})

const lensTechUpsell = computed(() => {
  if (customisedProduct.value) {
    return getClosestLensTechs(customisedProduct.value).upsell
  }

  return {}
})

const showlensTechUpsellOption = computed(() => {
  if (customisedProduct.value?.isLimitedEdition) return false
  if (customisedProduct.value?.isPrebuiltVariant) return false

  // If we are currently running a promo and this product is 8KO, then don't show the upsell
  if (automaticDiscountsForProduct.value) return false

  if (!lensTechUpsell.value.inStock && !hasUpgradedDetails.value) return false

  // don't show the polarisation upsell if it has already been 'upsold' on page load
  // (we don't want customer to downgrade after configuring polarisation earlier in the flow)
  if (!hasUpgradedDetails.value && !lensTechUpsell.value.sku) return false

  if (props.cartItem.bundleQuantity > 1) return false

  return customisedProduct.value?.allowLensTechFiltering
})


function buildFreeGiftAdjustedQuantity(newQuantity: number) {
  const freeGiftCartItem: Bundle = Object.values(shopifyStore.bundledCartItems).find(
    (value) => {
      return value.summary.bundleParentSku === customisedProduct.value.sku
    }
  )

  if (!freeGiftCartItem) return []

  return buildQtyChangePayload(freeGiftCartItem, newQuantity)
}

async function handleQtyChange(newQuantity: number) {
  isLoading.value = true

  const qtyChangePayload = [
    ...buildQtyChangePayload(props.cartItem.bundle, newQuantity),
  ]

  if (automaticDiscountsForProduct.value) {
    qtyChangePayload.push(...buildFreeGiftAdjustedQuantity(newQuantity))
  }

  await shopifyStore.updateCartItems(qtyChangePayload)
  isLoading.value = false
}

async function handleRemoveItem() {
  isLoading.value = true

  const skusToRemove = [
    customisedProduct.value.sku
  ]

  if (automaticDiscountsForProduct.value) {
    const freeGiftCartItem = Object.values(shopifyStore.bundledCartItems).find(
      (value) => {
        return value.summary.bundleParentSku === customisedProduct.value.sku
      }
    )

    if (freeGiftCartItem) {
      skusToRemove.push(freeGiftCartItem.summary.bundleSku)
    }
  }

  await shopifyStore.removeCartItems(skusToRemove)
  await $track.product.remove(customisedProduct.value)

  isLoading.value = false
}

function updateSkuLensTech(sku: any) {
  const regex = /^[a-z0-9]{1,2}le_/
  const splitSku = sku.split('-')

  return splitSku
    .map((item: any) => {
      if (regex.test(item)) return currentLensSku.value
      return item
    })
    .join('-')
}

function handleLensTechUpsellClick({
  selectedOption,
  downgrade,
}: {
  selectedOption: any
  downgrade: boolean
}) {
  isLoading.value = true
  handleLensOptionChange(selectedOption)

  const updatedSKU = updateSkuLensTech(props.cartItem.bundleSku)
  customisedProduct.value?.setChoicesFromSKU(updatedSKU, true, 'shopify')

  const newCartItems = buildCartItems(customisedProduct.value, [
    {
      key: '_bundleDisplayOrder',
      value: String(props.cartItem.bundleDisplayOrder || 0),
    },
  ])

  const oldCartItemsToRemove = props.cartItem.bundleItems.map(
    (item: any) => item.node.id
  )

  emits('polarisation-upgrade', {
    customisedProduct: customisedProduct.value,
    updatedSku: customisedProduct.value.sku,
    previousSku: props.cartItem.bundleSku,
    newCartItems,
    oldCartItemsToRemove,
    isDowngrade: downgrade,
  })
}

function handleLensOptionChange(selectedOption: any) {
  currentLensTech.value = selectedOption.tech
  currentLensSku.value = selectedOption.sku
  const updatedLensOptions = currentLensOptions.value.map((lensOption: any) => {
    return {
      ...lensOption,
      active: lensOption.tech === currentLensTech.value,
    }
  })
  currentLensOptions.value = updatedLensOptions
}

function handleImageLoad() {
  imageLoaded.value = true
}
</script>

<style lang="postcss">
.CartItem {
  @apply relative pb-4 border-b border-grey-100 last:border-none last:pb-0;

  &--is-loading {
    &::before {
      @apply content-[''] absolute inset-0 z-10;
    }
  }

  &:has(+ .CartDiscountedItem) {
    @apply border-0 pb-1;
  }

  &--sold-out {
    .CartItem__image-wrapper {
      img {
        @apply opacity-40;
      }
    }

    .CartItem__title {
      @apply text-grey-500;
    }
  }
}

.CartItem__out-of-stock-notice {
  @apply font-bold
}

.CartItem__container {
  @apply flex flex-row items-stretch
    relative w-full gap-4 md:gap-6;

  img {
    @apply w-full inline-block;
  }
}

.CartItem__image-wrapper {
  @apply relative
    w-24 md:w-xl flex-shrink-0 aspect-square;

  img {
    @apply w-full;
  }
}

.CartItem__product-image {
  @apply rounded-lg;
}

.CartItem__sub-title {
  @apply mt-2;
}

.CartItem__custom-title-prefix {
  @apply text-grey-500;
}

.CartItem__body {
  @apply w-full flex-grow flex flex-col justify-between gap-1 py-1;
}

.CartItem__body-head {
  @apply flex items-start justify-between gap-3;
}

.CartItem__body-head-title-container {
  @apply max-w-[200px];
}

.CartItem__body-main {
  @apply flex justify-between items-end gap-3;
}

.CartItem__extra-content-title {
  @apply cursor-pointer text-xxs;
}

.CartItem__extra-content-copy {
  @apply p-md h-full flex flex-col justify-center;
}

.CartItem__price-box {
  @apply w-fit overflow-hidden;

  &--is-loading {
    .CartItem__original-price,
    .CartItem__price {
      @apply translate-y-full;
    }
  }
}

.CartItem__original-price,
.CartItem__price {
  @apply inline-block transition-transform duration-300;
}

.CartItem__original-price {
  @apply text-xs mr-1.5 text-grey-500 delay-[50ms];
}

.CartItem__prescription-note {
  @apply text-xxs mt-4 text-grey-500;
}

.CartItem__le-tag {
  @apply mb-1;
}

.CartItem__prebuilt-variant-name {
  @apply font-thin text-[11px] md:text-[12px] text-grey-500;
}
</style>
